/**
 * UndoRedoManager - Undo/Redo state management
 * Manages a stack of schema states for undo/redo functionality
 * Phase 4: Enhanced with action descriptions for better UX
 */

import type { FormSchema } from '@atlas-forms/types-js';

interface HistoryEntry {
  state: FormSchema;
  description: string;
}

export class UndoRedoManager {
  private history: HistoryEntry[] = [];
  private currentIndex: number = -1;
  private maxSize: number = 100; // Maximum history size

  /**
   * Push a new state onto the history with an action description
   */
  push(state: FormSchema, description: string = 'Change'): void {
    // Remove any redo states if we're not at the end
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Add new state with description
    this.history.push({
      state: JSON.parse(JSON.stringify(state)),
      description,
    });
    this.currentIndex++;

    // Keep history size manageable
    if (this.history.length > this.maxSize) {
      this.history = this.history.slice(1);
      this.currentIndex--;
    }
  }

  /**
   * Undo to previous state
   */
  undo(): FormSchema | null {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      return JSON.parse(JSON.stringify(this.history[this.currentIndex].state));
    }
    return null;
  }

  /**
   * Redo to next state
   */
  redo(): FormSchema | null {
    if (this.currentIndex < this.history.length - 1) {
      this.currentIndex++;
      return JSON.parse(JSON.stringify(this.history[this.currentIndex].state));
    }
    return null;
  }

  /**
   * Get description of the action that will be undone
   */
  getUndoDescription(): string | null {
    if (this.currentIndex > 0) {
      return this.history[this.currentIndex].description;
    }
    return null;
  }

  /**
   * Get description of the action that will be redone
   */
  getRedoDescription(): string | null {
    if (this.currentIndex < this.history.length - 1) {
      return this.history[this.currentIndex + 1].description;
    }
    return null;
  }

  /**
   * Get current history length
   */
  getHistoryLength(): number {
    return this.history.length;
  }

  /**
   * Get redo length
   */
  getRedoLength(): number {
    return Math.max(0, this.history.length - this.currentIndex - 1);
  }

  /**
   * Clear history
   */
  clear(): void {
    this.history = [];
    this.currentIndex = -1;
  }

  /**
   * Get current state (at current index)
   */
  getCurrentState(): FormSchema | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return JSON.parse(JSON.stringify(this.history[this.currentIndex].state));
    }
    return null;
  }

  /**
   * Check if can undo
   */
  canUndo(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Check if can redo
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }
}
